/**
 * Presence Slice
 *
 * Manages real-time online/away status of workspace members.
 * Transient state — not persisted to localStorage.
 */

import type { MemberLocation } from '../../types/presence';

// ===== TYPES =====

export type PresenceStatus = 'online' | 'away';

// ===== STATE INTERFACE =====

export interface PresenceState {
  /** Status of users currently tracked in the active workspace */
  userStatuses: Record<string, PresenceStatus>;
  /** Whether the SignalR presence connection is active */
  isConnected: boolean;
  /** Live broadcast positions of members, keyed by member id. */
  memberLocations: Record<string, MemberLocation>;
}

// ===== ACTIONS INTERFACE =====

export interface PresenceActions {
  setOnlineUsers: (ids: string[]) => void;
  setUserStatuses: (statuses: Record<string, PresenceStatus>) => void;
  addOnlineUser: (id: string) => void;
  removeOnlineUser: (id: string) => void;
  setUserStatus: (id: string, status: PresenceStatus) => void;
  setPresenceConnected: (connected: boolean) => void;
  setMemberLocation: (id: string, location: MemberLocation) => void;
  removeMemberLocation: (id: string) => void;
  clearMemberLocations: () => void;
}

// ===== SLICE INTERFACE =====

export interface PresenceSlice {
  presence: PresenceState & PresenceActions;
}

// ===== INITIAL STATE =====

const initialPresenceState: PresenceState = {
  userStatuses: {},
  isConnected: false,
  memberLocations: {},
};

// ===== SLICE CREATOR =====

export const createPresenceSlice = (
  set: (fn: (state: PresenceSlice) => void) => void,
  _get: () => PresenceSlice,
  _store: unknown
) => ({
  presence: {
    ...initialPresenceState,

    setOnlineUsers: (ids: string[]) => {
      set((state) => {
        const statuses: Record<string, PresenceStatus> = {};
        for (const userId of ids) {
          statuses[userId] = 'online';
        }
        state.presence.userStatuses = statuses;
      });
    },

    setUserStatuses: (statuses: Record<string, PresenceStatus>) => {
      set((state) => {
        state.presence.userStatuses = statuses;
      });
    },

    addOnlineUser: (id: string) => {
      set((state) => {
        state.presence.userStatuses[id] = 'online';
      });
    },

    removeOnlineUser: (id: string) => {
      set((state) => {
        const { [id]: _removed, ...remaining } = state.presence.userStatuses;
        state.presence.userStatuses = remaining;
      });
    },

    setUserStatus: (id: string, status: PresenceStatus) => {
      set((state) => {
        state.presence.userStatuses[id] = status;
      });
    },

    setPresenceConnected: (connected: boolean) => {
      set((state) => {
        state.presence.isConnected = connected;
      });
    },

    setMemberLocation: (id: string, location: MemberLocation) => {
      set((state) => {
        state.presence.memberLocations[id] = location;
      });
    },

    removeMemberLocation: (id: string) => {
      set((state) => {
        const { [id]: _removed, ...remaining } = state.presence.memberLocations;
        state.presence.memberLocations = remaining;
      });
    },

    clearMemberLocations: () => {
      set((state) => {
        state.presence.memberLocations = {};
      });
    },
  },
});
